/**
 * useSearchData - Primary hook for accessing SharedDataStore data
 *
 * This hook fetches organizations, deals, and products from the search service,
 * which uses the SharedDataStore for efficient data fetching.
 *
 * Use this instead of useAllItemsBootstrap for components that need access to
 * organizations, deals, and products data.
 *
 * Benefits:
 * - Data is already cached by SharedDataStore on the server
 * - No duplicate fetching - shares data with grid components
 * - Faster initial load when grids have already been accessed
 *
 * Usage:
 * ```tsx
 * const { organizations, deals, products, isLoading } = useSearchData()
 * ```
 *
 * Invalidate with:
 * ```tsx
 * queryClient.invalidateQueries({ queryKey: SEARCH_DATA_KEY })
 * ```
 */

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { baseUrl } from 'api/config'
import axios from 'axios'
import { useMemo } from 'react'

const API_BASE = baseUrl || 'https://grobotic.app:2096'

/** Query key for search data - use for invalidation */
export const SEARCH_DATA_KEY = ['search', 'allData'] as const

export interface SearchDataMeta {
  ready: boolean
  orgsReady?: boolean
  dealsReady?: boolean
  productsReady?: boolean
  counts?: {
    organizations: number
    deals: number
    products: number
    parentClients: number
  }
  cacheAgeMs?: number | null
  lastFetchDurationMs?: number | null
  message?: string
}

export interface SearchDataResponse {
  organizations: any[]
  deals: any[]
  products: any[]
  parentClients: any[]
  meta: SearchDataMeta
}

/**
 * Fetch all data from search service
 */
const fetchSearchData = async (): Promise<SearchDataResponse> => {
  const response = await axios.get<SearchDataResponse>(`${API_BASE}/search/all`, {
    // Accept 503 status (indexes initializing) as valid response
    validateStatus: s => (s >= 200 && s < 300) || s === 503,
  })

  const fallback: SearchDataResponse = {
    organizations: [],
    deals: [],
    products: [],
    parentClients: [],
    meta: { ready: false, message: 'Empty response' },
  }

  const raw = response.data ?? fallback

  // Ensure all array fields exist (503 responses may omit them)
  return {
    ...raw,
    organizations: raw.organizations ?? [],
    deals: raw.deals ?? [],
    products: raw.products ?? [],
    parentClients: raw.parentClients ?? [],
    meta: raw.meta ?? fallback.meta,
  }
}

export interface UseSearchDataOptions {
  enabled?: boolean
  /** Poll interval when indexes aren't ready (default: 3000ms) */
  retryInterval?: number
  /** Stale time in ms (default: 30000ms) */
  staleTime?: number
}

export interface UseSearchDataReturn {
  // Data
  organizations: any[]
  menu_orgs: any[]
  deals: any[]
  products: any[]
  parentClients: any[]

  // Computed data (for bootstrap compatibility)
  custom_products: any[]

  // Loading state
  isLoading: boolean
  isFetching: boolean
  isReady: boolean
  error: string | null

  // Meta information
  meta: SearchDataMeta | null

  // Actions
  refresh: () => Promise<void>

  // Raw query data for advanced usage
  data: SearchDataResponse | undefined
}

/**
 * Hook for fetching all data from search service (SharedDataStore)
 *
 * This is the primary way to access organizations, deals, and products
 * data that has been fetched by the SharedDataStore.
 */
export const useSearchData = (options: UseSearchDataOptions = {}): UseSearchDataReturn => {
  const { enabled = true, retryInterval = 1000, staleTime = 30_000 } = options

  const queryClient = useQueryClient()

  const { data, isLoading, isFetching, error: queryError } = useQuery({
    queryKey: SEARCH_DATA_KEY,
    queryFn: fetchSearchData,
    enabled,
    staleTime,
    gcTime: 5 * 60_000, // Keep in cache for 5 minutes
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    // Retry more aggressively if indexes aren't ready
    retry: 3,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, retryInterval),
    // If data indicates indexes aren't ready, refetch after interval
    refetchInterval: query => {
      const queryData = query.state.data as SearchDataResponse | undefined
      if (queryData && !queryData.meta?.ready) {
        return retryInterval
      }
      return false // Stop polling once ready
    },
  })

  // Extract data
  const organizations = data?.organizations ?? []
  const deals = data?.deals ?? []
  const products = data?.products ?? []
  const parentClients = data?.parentClients ?? []

  // Deduplicated + slim org list for autocompletes / menus
  const menu_orgs = useMemo(() => {
    if (!organizations?.length) return []
    const seen = new Set<string>()
    const result: any[] = []
    for (const org of organizations) {
      const id = org?.org_uuid
      if (!id || seen.has(id)) continue
      seen.add(id)
      result.push({
        PK: org.PK,
        SK: org.SK,
        org_uuid: id,
        id: org.id,
        name: org.name,
        type: org.type ?? '',
        website: org.website,
        vertical: org.vertical ?? [],
        industry: org.industry ?? '',
      })
    }
    result.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
    return result
  }, [organizations])

  // Computed: custom products (for bootstrap compatibility)
  const custom_products = useMemo(() => {
    if (!products || !products.length) return []
    return products.filter(p => p?.SK?.startsWith('PRODUCT_CUSTOM_')).filter(Boolean)
  }, [products])

  const isReady = data?.meta?.ready ?? false
  const meta = data?.meta ?? null

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: SEARCH_DATA_KEY })
  }
  // console.log(
  //   'organizations',
  //   organizations?.filter(o => o?.name?.includes('Grobotic'))
  // )

  return {
    organizations,
    menu_orgs,
    deals,
    products,
    parentClients,
    custom_products,
    isLoading,
    isFetching,
    isReady,
    error: queryError?.message ?? null,
    meta,
    refresh,
    data,
  }
}

/**
 * useSyncStatus - Polls the search health endpoint for real-time sync progress.
 * Use this during loading to show which indexes have completed.
 * Stops polling once all indexes are ready.
 */
export interface SyncStatus {
  orgsReady: boolean
  dealsReady: boolean
  productsReady: boolean
  mtbReady: boolean
  orgCount: number
  dealCount: number
  productCount: number
  allReady: boolean
}

const fetchSyncStatus = async (): Promise<SyncStatus> => {
  try {
    // Use validateStatus to accept 503 as valid (indexes still loading)
    const { data } = await axios.get(`${API_BASE}/search/health`, {
      validateStatus: status => status === 200 || status === 503,
    })
    return {
      orgsReady: data.organizations?.ready ?? false,
      dealsReady: data.deals?.ready ?? false,
      productsReady: data.products?.ready ?? false,
      mtbReady: data.mtb?.ready ?? false,
      orgCount: data.organizations?.documentCount ?? 0,
      dealCount: data.deals?.documentCount ?? 0,
      productCount: data.products?.documentCount ?? 0,
      allReady: data.healthy ?? false,
    }
  } catch {
    return { orgsReady: false, dealsReady: false, productsReady: false, mtbReady: false, orgCount: 0, dealCount: 0, productCount: 0, allReady: false }
  }
}

export const useSyncStatus = (enabled = true) => {
  return useQuery<SyncStatus>({
    queryKey: ['search', 'syncStatus'],
    queryFn: fetchSyncStatus,
    enabled,
    staleTime: 0,
    refetchInterval: query => {
      const data = query.state.data
      if (data?.allReady) return false // Stop polling once ready
      return 1000 // Poll every second while loading
    },
  })
}

/**
 * Utility function to manually fetch search data (for non-React contexts)
 */
export const fetchAllSearchData = fetchSearchData

export default useSearchData
